import { useEffect, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { useReadyAuth } from "@/auth";
import {
  FEED_TABS,
  type FeedTab,
  type SignalTypeFilter as TypeFilter,
  type SortValue,
} from "@/features/signals/types";
import { SIGNAL_TYPE_FILTERS } from "@/features/signals/score";
import { resolvePracticeFromUrl, type PracticeKey } from "@/features/signals/practice-lens-ui";
import { useSignalFeed, usePracticeRegistry } from "@/features/signals/hooks";
import { SignalsHeader } from "@/features/signals/components/SignalsHeader";
import { SignalTypeFilter } from "@/features/signals/components/SignalTypeFilter";
import { OwnershipTabs } from "@/features/signals/components/OwnershipTabs";
import { SignalFeed } from "@/features/signals/components/SignalFeed";

/**
 * F1 · SignalsPage — the Phase D feed route container. The active ownership tab
 * (?tab=) and signal-type filter (?type=) both live in the URL, so the feed is
 * deep-linkable and the browser back button moves between views. The detail
 * modal at /signals/:id renders through the Outlet; the feed stays mounted
 * beneath it.
 *
 * Feed restyle: the mono triage strip is replaced by the navy glass stats box
 * (SignalsHeader) plus the signal-type filter sub-box. The two filter
 * dimensions compose — the tab predicate and the type filter both flow into the
 * feed adapter's getFeedPage params.
 */

function coerceTab(value: string | null): FeedTab {
  const keys = FEED_TABS.map((t) => t.key) as string[];
  return value && keys.includes(value) ? (value as FeedTab) : "all";
}

function coerceType(value: string | null): TypeFilter {
  const keys = SIGNAL_TYPE_FILTERS.map((f) => f.key) as string[];
  return value && keys.includes(value) ? (value as TypeFilter) : "all";
}

export function SignalsPage(): JSX.Element {
  const { user, profile } = useReadyAuth();
  const viewerId = user.id;
  const isAdmin = profile.role === "admin" || profile.role === "super_admin";

  const [params, setParams] = useSearchParams();
  const activeTab = coerceTab(params.get("tab"));
  const activeType = coerceType(params.get("type"));
  // Sort is session-local (Robert's ruling: reset on a fresh visit) — not in the
  // URL like tab/type. It enters only the feed query and its key.
  const [sort, setSort] = useState<SortValue>("most_recent");

  // Practice lens (2W) — ?practice=<canonical key>, validated only against the
  // LOADED registry. Before the registry loads (or on a load failure) this
  // resolves to null (All), so a practice-filtered feed request is never
  // issued against an unvalidated value.
  const registry = usePracticeRegistry(viewerId);
  const rawPracticeParam = params.get("practice");
  const selectedPractice: PracticeKey | null = registry.data
    ? resolvePracticeFromUrl(rawPracticeParam, registry.data)
    : null;

  // An unknown, alias, padded, cased or inactive value is removed from the URL
  // once the registry has loaded and proven it invalid — the view already
  // stayed on All above, so nothing was ever sent to the server with it.
  useEffect(() => {
    if (!registry.data || !rawPracticeParam) return;
    if (resolvePracticeFromUrl(rawPracticeParam, registry.data) !== null) return;
    const next = new URLSearchParams(params);
    next.delete("practice");
    setParams(next, { replace: true });
  }, [registry.data, rawPracticeParam, params, setParams]);

  const feed = useSignalFeed(activeTab, viewerId, activeType, sort, selectedPractice);

  // Nav search (?q=) — filter the loaded feed rows on company + headline. The
  // feed adapter has no text argument, so this narrows the already-fetched pages
  // client-side, layered on top of the server-side tab + type filters (it runs
  // strictly after them, so it can't break either). Note: only loaded pages are
  // searched; matches on unfetched pages appear as more pages load.
  const q = (params.get("q") ?? "").trim().toLowerCase();
  const visibleSignals = q
    ? feed.signals.filter(
        (s) =>
          s.company.toLowerCase().includes(q) ||
          s.headline.toLowerCase().includes(q),
      )
    : feed.signals;

  const setTab = (tab: FeedTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next, { replace: true });
  };

  const setType = (type: TypeFilter) => {
    const next = new URLSearchParams(params);
    if (type === "all") next.delete("type");
    else next.set("type", type);
    setParams(next, { replace: true });
  };

  // Selecting a practice restarts cursor pagination: practiceKey is part of
  // the feed query key (see useSignalFeed), and the SignalFeed remount key
  // below also carries it.
  const setPractice = (practiceKey: PracticeKey | null) => {
    const next = new URLSearchParams(params);
    if (practiceKey === null) next.delete("practice");
    else next.set("practice", practiceKey);
    setParams(next, { replace: true });
  };

  // Empty-state "Clear filters" — reset every dimension (tab, type, practice,
  // and the nav search term) in one URL write so the feed refills.
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    next.delete("type");
    next.delete("q");
    next.delete("practice");
    next.set("tab", "all");
    setParams(next, { replace: true });
  };

  // The explicit "not yet routed" empty state (spec C) fires only on the
  // server-truth practice_lens.total_count — never on the nav search
  // narrowing the currently-loaded page to zero locally.
  const selectedPracticeInfo = selectedPractice
    ? registry.data?.find((p) => p.practiceKey === selectedPractice) ?? null
    : null;
  const practiceEmptyDisplayName =
    selectedPracticeInfo && feed.status === "success" &&
      (feed.practiceLens?.totalCount ?? 0) === 0
      ? selectedPracticeInfo.displayName
      : null;

  return (
    <>
      <SignalsHeader
        viewerId={viewerId}
        isAdmin={isAdmin}
        tab={activeTab}
        signalType={activeType}
        sort={sort}
        onSortChange={setSort}
        practices={registry.data ?? []}
        practicesStatus={registry.status}
        selectedPractice={selectedPractice}
        onPracticeChange={setPractice}
        onRetryPractices={registry.refetch}
      />
      <SignalTypeFilter active={activeType} onChange={setType} />
      <OwnershipTabs active={activeTab} onChange={setTab} viewerId={viewerId} />

      <SignalFeed
        key={`${activeTab}:${activeType}:${sort}:${selectedPractice ?? "all"}`}
        signals={visibleSignals}
        total={q ? visibleSignals.length : feed.total}
        status={feed.status}
        hasNextPage={q ? false : feed.hasNextPage}
        isFetchingNextPage={feed.isFetchingNextPage}
        fetchNextPage={feed.fetchNextPage}
        onRetry={feed.refetch}
        onClearFilters={clearFilters}
        viewerId={viewerId}
        practiceEmptyDisplayName={q ? null : practiceEmptyDisplayName}
      />

      {/* Detail modal at /signals/:id renders here as an overlay; the feed
          above stays mounted so scroll is preserved when it closes. */}
      <Outlet />
    </>
  );
}
